/**
 * Base roles stored on a user. Contextual roles such as "class teacher of 8-A" or
 * "guardian of this student" are derived by Cerbos from attributes, never stored here.
 * The full catalogue of 49 role templates lives in the master plan; add them here as
 * modules ship, together with a Cerbos policy and tests.
 */
export const ROLES = [
  "platform_admin",
  "platform_head_office",
  "platform_state_manager",
  "platform_regional_manager",
  "platform_school_manager",
  "platform_onboarding",
  "platform_billing",
  "platform_support",
  "platform_operations",
  "institution_admin",
  "principal",
  "teacher",
  "accountant",
  "parent",
  "student",
] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABELS: Record<Role, string> = {
  platform_admin: "Super admin",
  platform_head_office: "Head office",
  platform_state_manager: "State Manager",
  platform_regional_manager: "Regional Manager",
  platform_school_manager: "School Manager",
  platform_onboarding: "Onboarding",
  platform_billing: "Billing",
  platform_support: "School support",
  platform_operations: "School control",
  institution_admin: "School admin",
  principal: "Principal",
  teacher: "Teacher",
  accountant: "Accountant",
  parent: "Parent",
  student: "Student",
};

export function isRole(value: unknown): value is Role {
  return typeof value === "string" && (ROLES as readonly string[]).includes(value);
}

/**
 * Where a team member works: which schools they cover. Every team member except the super
 * admin holds exactly one. Head office covers every school; the managers cover the states,
 * regions or schools assigned to them (their "area"), minus any schools excluded from it.
 */
export const PLATFORM_POSITIONS = [
  "platform_head_office",
  "platform_state_manager",
  "platform_regional_manager",
  "platform_school_manager",
] as const satisfies readonly Role[];

export type PlatformPosition = (typeof PLATFORM_POSITIONS)[number];

/** What a team member may do in the schools they cover. Any combination; none means view only. */
export const PLATFORM_PERMISSIONS = [
  "platform_onboarding",
  "platform_billing",
  "platform_support",
  "platform_operations",
] as const satisfies readonly Role[];

export type PlatformPermission = (typeof PLATFORM_PERMISSIONS)[number];

/**
 * Roles for Eduleo's own team (the platform console). They only take effect for people signed
 * in to the EDULEO institution; Cerbos checks that, so a school can't grant them to itself.
 */
export const PLATFORM_ROLES = [
  "platform_admin",
  ...PLATFORM_POSITIONS,
  ...PLATFORM_PERMISSIONS,
] as const satisfies readonly Role[];

export type PlatformRole = (typeof PLATFORM_ROLES)[number];

export const PLATFORM_ROLE_DESCRIPTIONS: Record<PlatformRole, string> = {
  platform_admin:
    "Every school and everything, including the team, regions and everyone's permissions.",
  platform_head_office: "Every school in India, apart from any you exclude.",
  platform_state_manager: "All schools in the states you choose, apart from any you exclude.",
  platform_regional_manager: "All schools in the regions you choose, apart from any you exclude.",
  platform_school_manager: "Only the schools you choose.",
  platform_onboarding: "Add schools, edit their details, check documents and prepare contracts.",
  platform_billing: "Create and send invoices, record payments and cancel invoices.",
  platform_support: "Help schools with sign-in: add accounts, reset passwords, turn accounts off.",
  platform_operations: "Suspend or reactivate schools and change their plan.",
};

/** Permissions ticked for a new team member in each position. The super admin can change them. */
export const DEFAULT_POSITION_PERMISSIONS: Record<PlatformPosition, readonly PlatformPermission[]> =
  {
    platform_head_office: [],
    platform_state_manager: [
      "platform_onboarding",
      "platform_billing",
      "platform_support",
      "platform_operations",
    ],
    platform_regional_manager: ["platform_onboarding", "platform_billing", "platform_support"],
    platform_school_manager: ["platform_support"],
  };

export function isPlatformPosition(value: unknown): value is PlatformPosition {
  return typeof value === "string" && (PLATFORM_POSITIONS as readonly string[]).includes(value);
}

export function isPlatformPermission(value: unknown): value is PlatformPermission {
  return typeof value === "string" && (PLATFORM_PERMISSIONS as readonly string[]).includes(value);
}

/** Roles a school can give its own people. Platform roles are never among them. */
export const SCHOOL_ROLES = [
  "institution_admin",
  "principal",
  "teacher",
  "accountant",
  "parent",
  "student",
] as const satisfies readonly Role[];

export type SchoolRole = (typeof SCHOOL_ROLES)[number];

/**
 * School roles that can see every student record. Only Eduleo's super admin may give them out
 * or reset such an account's password; support handles everyone else.
 */
export const PRIVILEGED_SCHOOL_ROLES: readonly SchoolRole[] = ["institution_admin", "principal"];

export function isPlatformRole(value: unknown): value is PlatformRole {
  return typeof value === "string" && (PLATFORM_ROLES as readonly string[]).includes(value);
}

export function isSchoolRole(value: unknown): value is SchoolRole {
  return typeof value === "string" && (SCHOOL_ROLES as readonly string[]).includes(value);
}

/** Institution code reserved for the platform operator's own staff. */
export const PLATFORM_INSTITUTION_CODE = "EDULEO";
